import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { getBizColor } from '../../utils/colors'

const EQUIPMENT_TYPES = ['Skid Steer', 'Trailer', 'Dump Truck', 'Dingo', 'Mulching Attachment', 'Chipper', 'Grapple', 'Grapple Bucket', 'Truck', 'Other']
const SERVICE_TYPES = ['Oil Change', 'Filter Change', 'Annual Inspection', 'Repair', 'Tire Service', 'Other']

export default function EquipmentPage() {
  const { profile, activeBusiness } = useAuth()
  const [equipment, setEquipment] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedEquip, setSelectedEquip] = useState(null)
  const [showAdd, setShowAdd] = useState(false)
  const [showCalc, setShowCalc] = useState(false)
  const role = profile?.role
  const canEdit = role === 'owner' || role === 'office_staff' || role === 'project_manager'

  const fetchEquipment = useCallback(async () => {
    setLoading(true)
    let query = supabase.from('equipment').select('*').eq('status', 'active').order('name')
    if (activeBusiness !== 'all') query = query.eq('business_id', activeBusiness)
    const { data, error } = await query
    if (error) console.error('Fetch equipment error:', error.message)
    setEquipment(data || [])
    setLoading(false)
  }, [activeBusiness])

  useEffect(() => { fetchEquipment() }, [fetchEquipment])

  const getOwnershipLabel = (equip) => {
    if (equip.is_financed) return 'FINANCED'
    if (equip.is_owned_free_clear) return 'OWNED'
    return 'OWNED'
  }

  const getOwnershipColor = (equip) => {
    return equip.is_financed ? '#e05c5c' : '#4a8c6a'
  }

  return (
    <div style={styles.container}>
      {selectedEquip && (
        <EquipmentDetail
          equip={selectedEquip}
          onClose={() => setSelectedEquip(null)}
          onUpdate={() => { fetchEquipment(); setSelectedEquip(null) }}
          canEdit={canEdit}
          profile={profile}
        />
      )}
      {showAdd && (
        <AddEquipmentModal
          activeBusiness={activeBusiness !== 'all' ? activeBusiness : 'riverbend'}
          onClose={() => setShowAdd(false)}
          onSave={() => { fetchEquipment(); setShowAdd(false) }}
        />
      )}
      {showCalc && <RentVsOwnCalc onClose={() => setShowCalc(false)} />}

      <div style={styles.header}>
        <div>
          <div style={styles.title}>EQUIPMENT</div>
          <div style={styles.subtitle}>{equipment.length} machines</div>
        </div>
        <div style={styles.headerBtns}>
          <button onClick={() => setShowCalc(true)} style={styles.calcBtn}>RENT VS OWN</button>
          {canEdit && <button onClick={() => setShowAdd(true)} style={styles.addBtn}>+ ADD EQUIPMENT</button>}
        </div>
      </div>

      {loading ? (
        <div style={styles.empty}>Loading equipment...</div>
      ) : equipment.length === 0 ? (
        <div style={styles.empty}>No equipment found.</div>
      ) : (
        <div style={styles.grid}>
          {equipment.map(equip => {
            const valueAge = equip.resale_updated_at
              ? Math.floor((new Date() - new Date(equip.resale_updated_at)) / (1000 * 60 * 60 * 24))
              : null
            const needsValuation = valueAge === null || valueAge >= 90
            return (
              <div
                key={equip.id}
                onClick={() => setSelectedEquip(equip)}
                style={{ ...styles.equipCard, borderTop: '3px solid ' + getBizColor(equip.business_id) }}
              >
                <div style={styles.equipHeader}>
                  <div style={styles.equipType}>{equip.type || 'EQUIPMENT'}</div>
                  <div style={{ ...styles.ownershipBadge, color: getOwnershipColor(equip) }}>
                    {getOwnershipLabel(equip)}
                  </div>
                </div>
                <div style={styles.equipName}>{equip.name}</div>
                <div style={styles.equipSub}>{equip.year} {equip.make} {equip.model}</div>
                {equip.estimated_resale && (
                  <div style={styles.equipValue}>
                    Est. Value: <span style={{ color: '#b48c3c' }}>${Number(equip.estimated_resale).toLocaleString()}</span>
                    {needsValuation && <span style={styles.valuationAlert}>{' ' + String.fromCodePoint(0x00B7) + ' UPDATE DUE'}</span>}
                  </div>
                )}
                {equip.is_financed && equip.monthly_payment && (
                  <div style={styles.equipPayment}>{String.fromCodePoint(0x1F4B3)} ${Number(equip.monthly_payment).toLocaleString()}/mo</div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function EquipmentDetail({ equip, onClose, onUpdate, canEdit, profile }) {
  const [tab, setTab] = useState('details')
  const [serviceLogs, setServiceLogs] = useState([])
  const [valueHistory, setValueHistory] = useState([])
  const [jobLog, setJobLog] = useState([])
  const [showService, setShowService] = useState(false)
  const [estimating, setEstimating] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState(equip)
  const [saving, setSaving] = useState(false)
  const setEdit = (k, v) => setEditForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    const fetchLogs = async () => {
      const { data: sl } = await supabase
        .from('equipment_service_logs')
        .select('*, profiles:logged_by(full_name)')
        .eq('equipment_id', equip.id)
        .order('service_date', { ascending: false })
      const { data: vh } = await supabase
        .from('equipment_value_history')
        .select('*')
        .eq('equipment_id', equip.id)
        .order('created_at', { ascending: false })
      const { data: jl } = await supabase
        .from('equipment_job_log')
        .select('*, jobs(client_name, start_date, site_address)')
        .eq('equipment_id', equip.id)
        .order('date_used', { ascending: false })
        .limit(20)
      setServiceLogs(sl || [])
      setValueHistory(vh || [])
      setJobLog(jl || [])
    }
    fetchLogs()
  }, [equip.id])

  const handleSaveEdit = async () => {
    setSaving(true)
    const { error } = await supabase.from('equipment').update({
      name: editForm.name,
      type: editForm.type,
      year: editForm.year ? parseInt(editForm.year) : null,
      make: editForm.make,
      model: editForm.model,
      serial_number: editForm.serial_number,
      is_financed: editForm.is_financed || false,
      is_owned_free_clear: !editForm.is_financed,
      lender: editForm.lender || null,
      loan_balance: editForm.loan_balance ? parseFloat(editForm.loan_balance) : null,
      loan_amount: editForm.loan_amount ? parseFloat(editForm.loan_amount) : null,
      interest_rate: editForm.interest_rate ? parseFloat(editForm.interest_rate) : null,
      monthly_payment: editForm.monthly_payment ? parseFloat(editForm.monthly_payment) : null,
      payment_due_day: editForm.payment_due_day ? parseInt(editForm.payment_due_day) : null,
      loan_payoff_date: editForm.loan_payoff_date || null,
      purchase_price: editForm.purchase_price ? parseFloat(editForm.purchase_price) : null,
      purchase_date: editForm.purchase_date || null,
      current_hours: editForm.current_hours ? parseFloat(editForm.current_hours) : null,
      notes: editForm.notes,
    }).eq('id', equip.id)
    if (error) console.error('Save error:', error.message)
    setSaving(false)
    setEditing(false)
    onUpdate()
  }

  const handleGetEstimate = async () => {
    setEstimating(true)
    try {
      const prompt = 'You are an equipment valuation expert. Estimate the current market value of the following piece of equipment based on typical resale values. Be specific and give a dollar range.\n\n' +
        'Equipment: ' + equip.year + ' ' + equip.make + ' ' + equip.model + '\n' +
        'Type: ' + (equip.type || 'Unknown') + '\n' +
        'Serial: ' + (equip.serial_number || 'Unknown') + '\n' +
        'Purchase Price: ' + (equip.purchase_price ? '$' + Number(equip.purchase_price).toLocaleString() : 'Unknown') + '\n' +
        'Purchase Date: ' + (equip.purchase_date || 'Unknown') + '\n' +
        'Current Hours: ' + (equip.current_hours || 'Unknown') + '\n\n' +
        'Provide:\n1. Estimated current market value range (low and high)\n2. Key factors affecting this valuation\n3. Brief note on market conditions for this type of equipment\n\nKeep response concise and practical.'

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          messages: [{ role: 'user', content: prompt }]
        })
      })
      const data = await response.json()
      const text = data.content?.[0]?.text || 'Unable to estimate value at this time.'

      const valueMatch = text.match(/\$[\d,]+/)
      const estimatedVal = valueMatch ? parseInt(valueMatch[0].replace(/[$,]/g, '')) : null

      await supabase.from('equipment_value_history').insert({
        equipment_id: equip.id,
        estimated_value: estimatedVal,
        source: 'AI Estimate',
        notes: text,
        logged_by: profile?.id,
      })

      if (estimatedVal) {
        await supabase.from('equipment').update({
          estimated_resale: estimatedVal,
          resale_updated_at: new Date().toISOString().split('T')[0],
        }).eq('id', equip.id)
      }

      const { data: vh } = await supabase
        .from('equipment_value_history')
        .select('*')
        .eq('equipment_id', equip.id)
        .order('created_at', { ascending: false })
      setValueHistory(vh || [])
      alert(text)
    } catch (err) {
      alert('Error getting estimate: ' + err.message)
    }
    setEstimating(false)
  }

  const dot = ' ' + String.fromCodePoint(0x00B7) + ' '

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.detailCard} onClick={e => e.stopPropagation()}>
        <div style={styles.detailHeader}>
          <div>
            <div style={styles.detailType}>{equip.type || 'EQUIPMENT'}</div>
            <div style={styles.detailName}>{equip.name}</div>
            <div style={styles.detailSub}>{equip.year} {equip.make} {equip.model}</div>
          </div>
          <div style={styles.detailHeaderRight}>
            {canEdit && (
              <button onClick={() => setEditing(!editing)} style={styles.editBtn}>
                {editing ? 'CANCEL' : 'EDIT'}
              </button>
            )}
            <button onClick={onClose} style={styles.closeBtn}>{String.fromCodePoint(0x2715)}</button>
          </div>
        </div>

        <div style={styles.tabs}>
          {['details', 'service', 'value', 'jobs'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }}
            >
              {t.toUpperCase()}
            </button>
          ))}
        </div>

        <div style={styles.detailBody}>
          {tab === 'details' && (
            editing ? (
              <div style={styles.editForm}>
                <div style={styles.editGrid}>
                  <div style={styles.field}><label style={styles.label}>NAME</label><input value={editForm.name || ''} onChange={e => setEdit('name', e.target.value)} style={styles.input} /></div>
                  <div style={styles.field}><label style={styles.label}>TYPE</label>
                    <select value={editForm.type || ''} onChange={e => setEdit('type', e.target.value)} style={styles.input}>
                      <option value="">Select...</option>
                      {EQUIPMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div style={styles.field}><label style={styles.label}>YEAR</label><input value={editForm.year || ''} onChange={e => setEdit('year', e.target.value)} style={styles.input} type="number" /></div>
                  <div style={styles.field}><label style={styles.label}>MAKE</label><input value={editForm.make || ''} onChange={e => setEdit('make', e.target.value)} style={styles.input} /></div>
                  <div style={styles.field}><label style={styles.label}>MODEL</label><input value={editForm.model || ''} onChange={e => setEdit('model', e.target.value)} style={styles.input} /></div>
                  <div style={styles.field}><label style={styles.label}>SERIAL NUMBER</label><input value={editForm.serial_number || ''} onChange={e => setEdit('serial_number', e.target.value)} style={styles.input} /></div>
                  <div style={styles.field}><label style={styles.label}>CURRENT HOURS</label><input value={editForm.current_hours || ''} onChange={e => setEdit('current_hours', e.target.value)} style={styles.input} type="number" /></div>
                  <div style={styles.field}><label style={styles.label}>FINANCED?</label>
                    <select value={editForm.is_financed ? 'yes' : 'no'} onChange={e => setEdit('is_financed', e.target.value === 'yes')} style={styles.input}>
                      <option value="no">No - Owned Free & Clear</option>
                      <option value="yes">Yes - Financed</option>
                    </select>
                  </div>
                  <div style={styles.field}><label style={styles.label}>PURCHASE PRICE</label><input value={editForm.purchase_price || ''} onChange={e => setEdit('purchase_price', e.target.value)} style={styles.input} type="number" /></div>
                  <div style={styles.field}><label style={styles.label}>PURCHASE DATE</label><input value={editForm.purchase_date || ''} onChange={e => setEdit('purchase_date', e.target.value)} style={styles.input} type="date" /></div>
                  {editForm.is_financed && <>
                    <div style={styles.field}><label style={styles.label}>LENDER</label><input value={editForm.lender || ''} onChange={e => setEdit('lender', e.target.value)} style={styles.input} /></div>
                    <div style={styles.field}><label style={styles.label}>ORIGINAL LOAN AMOUNT</label><input value={editForm.loan_amount || ''} onChange={e => setEdit('loan_amount', e.target.value)} style={styles.input} type="number" /></div>
                    <div style={styles.field}><label style={styles.label}>INTEREST RATE (%)</label><input value={editForm.interest_rate || ''} onChange={e => setEdit('interest_rate', e.target.value)} style={styles.input} type="number" step="0.1" /></div>
                    <div style={styles.field}><label style={styles.label}>LOAN BALANCE</label><input value={editForm.loan_balance || ''} onChange={e => setEdit('loan_balance', e.target.value)} style={styles.input} type="number" /></div>
                    <div style={styles.field}><label style={styles.label}>MONTHLY PAYMENT</label><input value={editForm.monthly_payment || ''} onChange={e => setEdit('monthly_payment', e.target.value)} style={styles.input} type="number" /></div>
                    <div style={styles.field}><label style={styles.label}>PAYMENT DUE DAY</label><input value={editForm.payment_due_day || ''} onChange={e => setEdit('payment_due_day', e.target.value)} style={styles.input} type="number" min="1" max="31" placeholder="1-31" /></div>
                    <div style={styles.field}><label style={styles.label}>PAYOFF DATE</label><input value={editForm.loan_payoff_date || ''} onChange={e => setEdit('loan_payoff_date', e.target.value)} style={styles.input} type="date" /></div>
                  </>}
                </div>
                <div style={styles.field}><label style={styles.label}>NOTES</label><textarea value={editForm.notes || ''} onChange={e => setEdit('notes', e.target.value)} style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }} /></div>
                <div style={styles.editActions}>
                  <button onClick={() => setEditing(false)} style={styles.cancelBtn}>CANCEL</button>
                  <button onClick={handleSaveEdit} disabled={saving} style={styles.saveBtn}>{saving ? 'SAVING...' : 'SAVE'}</button>
                </div>
              </div>
            ) : (
              <div>
                <div style={styles.infoGrid}>
                  <InfoItem label="SERIAL NUMBER" value={equip.serial_number} />
                  <InfoItem label="STATUS" value={equip.is_financed ? 'FINANCED' : 'OWNED'} />
                  <InfoItem label="CURRENT HOURS" value={equip.current_hours ? Number(equip.current_hours).toLocaleString() : null} />
                  <InfoItem label="PURCHASE PRICE" value={equip.purchase_price ? '$' + Number(equip.purchase_price).toLocaleString() : null} />
                  <InfoItem label="PURCHASE DATE" value={equip.purchase_date} />
                  {equip.is_financed && <>
                    <InfoItem label="LENDER" value={equip.lender} />
                    <InfoItem label="LOAN BALANCE" value={equip.loan_balance ? '$' + Number(equip.loan_balance).toLocaleString() : null} />
                    <InfoItem label="MONTHLY PAYMENT" value={equip.monthly_payment ? '$' + Number(equip.monthly_payment).toLocaleString() + '/mo' : null} />
                    <InfoItem label="INTEREST RATE" value={equip.interest_rate ? equip.interest_rate + '%' : null} />
                    <InfoItem label="PAYOFF DATE" value={equip.loan_payoff_date} />
                  </>}
                </div>
                {equip.notes && <div style={styles.notes}>{equip.notes}</div>}
              </div>
            )
          )}

          {tab === 'service' && (
            <div>
              {canEdit && <button onClick={() => setShowService(true)} style={styles.addLogBtn}>+ LOG SERVICE</button>}
              {serviceLogs.length === 0 ? (
                <div style={styles.emptyTab}>No service logs yet.</div>
              ) : serviceLogs.map(log => (
                <div key={log.id} style={styles.logRow}>
                  <div style={styles.logLeft}>
                    <div style={styles.logType}>{log.service_type}</div>
                    <div style={styles.logDate}>{log.service_date}{dot}{log.profiles?.full_name}</div>
                    {log.notes && <div style={styles.logNotes}>{log.notes}</div>}
                    {log.next_service_date && <div style={styles.logNext}>Next due: {log.next_service_date}</div>}
                  </div>
                  {log.cost && <div style={styles.logCost}>${Number(log.cost).toLocaleString()}</div>}
                </div>
              ))}
            </div>
          )}

          {tab === 'value' && (
            <div>
              <div style={styles.valueActions}>
                <button onClick={handleGetEstimate} disabled={estimating} style={styles.estimateBtn}>
                  {estimating ? 'ESTIMATING...' : String.fromCodePoint(0x1F916) + ' GET AI VALUE ESTIMATE'}
                </button>
              </div>
              {equip.estimated_resale && (
                <div style={styles.currentValue}>
                  <div style={styles.currentValueLabel}>CURRENT ESTIMATED VALUE</div>
                  <div style={styles.currentValueAmount}>${Number(equip.estimated_resale).toLocaleString()}</div>
                  {equip.resale_updated_at && <div style={styles.currentValueDate}>Last updated {new Date(equip.resale_updated_at).toLocaleDateString()}</div>}
                </div>
              )}
              {valueHistory.length === 0 ? (
                <div style={styles.emptyTab}>No value history yet.</div>
              ) : valueHistory.map(v => (
                <div key={v.id} style={styles.logRow}>
                  <div style={styles.logLeft}>
                    <div style={styles.logType}>{v.source}</div>
                    <div style={styles.logDate}>{new Date(v.created_at).toLocaleDateString()}</div>
                    {v.notes && <div style={styles.logNotes}>{v.notes.substring(0, 150)}{v.notes.length > 150 ? '...' : ''}</div>}
                  </div>
                  {v.estimated_value && <div style={styles.logCost}>${Number(v.estimated_value).toLocaleString()}</div>}
                </div>
              ))}
            </div>
          )}

          {tab === 'jobs' && (
            <div>
              {jobLog.length === 0 ? (
                <div style={styles.emptyTab}>No job history yet.</div>
              ) : jobLog.map(j => (
                <div key={j.id} style={styles.logRow}>
                  <div style={styles.logLeft}>
                    <div style={styles.logType}>{j.jobs?.client_name}</div>
                    <div style={styles.logDate}>{j.date_used}{dot}{j.jobs?.site_address}</div>
                    {j.notes && <div style={styles.logNotes}>{j.notes}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showService && (
          <ServiceLogModal
            equipId={equip.id}
            profileId={profile?.id}
            onClose={() => setShowService(false)}
            onSave={async () => {
              setShowService(false)
              const { data: sl } = await supabase
                .from('equipment_service_logs')
                .select('*, profiles:logged_by(full_name)')
                .eq('equipment_id', equip.id)
                .order('service_date', { ascending: false })
              setServiceLogs(sl || [])
            }}
          />
        )}
      </div>
    </div>
  )
}

function InfoItem({ label, value }) {
  if (!value) return null
  return (
    <div style={styles.infoItem}>
      <div style={styles.infoLabel}>{label}</div>
      <div style={styles.infoValue}>{value}</div>
    </div>
  )
}

function AddEquipmentModal({ activeBusiness, onClose, onSave }) {
  const [form, setForm] = useState({
    name: '', type: 'Truck', year: '', make: '', model: '', serial_number: '',
    business_id: activeBusiness, is_financed: false,
    lender: '', loan_amount: '', interest_rate: '', loan_balance: '', monthly_payment: '',
    payment_due_day: '', loan_payoff_date: '',
    purchase_price: '', purchase_date: '', current_hours: '', notes: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleSave = async () => {
    if (!form.name) { setError('Equipment name is required.'); return }
    setSaving(true)
    setError('')
    const { error: err } = await supabase.from('equipment').insert({
      name: form.name,
      type: form.type,
      year: form.year ? parseInt(form.year) : null,
      make: form.make || null,
      model: form.model || null,
      serial_number: form.serial_number || null,
      business_id: form.business_id,
      status: 'active',
      is_financed: form.is_financed,
      is_owned_free_clear: !form.is_financed,
      lender: form.lender || null,
      loan_amount: form.loan_amount ? parseFloat(form.loan_amount) : null,
      interest_rate: form.interest_rate ? parseFloat(form.interest_rate) : null,
      loan_balance: form.loan_balance ? parseFloat(form.loan_balance) : null,
      monthly_payment: form.monthly_payment ? parseFloat(form.monthly_payment) : null,
      payment_due_day: form.payment_due_day ? parseInt(form.payment_due_day) : null,
      loan_payoff_date: form.loan_payoff_date || null,
      purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : null,
      purchase_date: form.purchase_date || null,
      current_hours: form.current_hours ? parseFloat(form.current_hours) : null,
      notes: form.notes || null,
    })
    if (err) { setError(err.message); setSaving(false); return }
    onSave()
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitle}>ADD EQUIPMENT</div>
          <button onClick={onClose} style={styles.closeBtn}>{String.fromCodePoint(0x2715)}</button>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.editGrid}>
            <div style={styles.field}><label style={styles.label}>NAME *</label><input value={form.name} onChange={e => set('name', e.target.value)} style={styles.input} placeholder="e.g. Skid Steer #1" /></div>
            <div style={styles.field}><label style={styles.label}>TYPE</label>
              <select value={form.type} onChange={e => set('type', e.target.value)} style={styles.input}>
                {EQUIPMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={styles.field}><label style={styles.label}>BUSINESS</label>
              <select value={form.business_id} onChange={e => set('business_id', e.target.value)} style={styles.input}>
                <option value="pools">CP Quality Pools</option>
                <option value="riverbend">Riverbend Clearing</option>
              </select>
            </div>
            <div style={styles.field}><label style={styles.label}>YEAR</label><input value={form.year} onChange={e => set('year', e.target.value)} style={styles.input} type="number" placeholder="2020" /></div>
            <div style={styles.field}><label style={styles.label}>MAKE</label><input value={form.make} onChange={e => set('make', e.target.value)} style={styles.input} placeholder="Bobcat" /></div>
            <div style={styles.field}><label style={styles.label}>MODEL</label><input value={form.model} onChange={e => set('model', e.target.value)} style={styles.input} placeholder="S650" /></div>
            <div style={styles.field}><label style={styles.label}>SERIAL NUMBER</label><input value={form.serial_number} onChange={e => set('serial_number', e.target.value)} style={styles.input} /></div>
            <div style={styles.field}><label style={styles.label}>CURRENT HOURS</label><input value={form.current_hours} onChange={e => set('current_hours', e.target.value)} style={styles.input} type="number" placeholder="0" /></div>
            <div style={styles.field}><label style={styles.label}>FINANCED?</label>
              <select value={form.is_financed ? 'yes' : 'no'} onChange={e => set('is_financed', e.target.value === 'yes')} style={styles.input}>
                <option value="no">No - Owned Free & Clear</option>
                <option value="yes">Yes - Financed</option>
              </select>
            </div>
            <div style={styles.field}><label style={styles.label}>PURCHASE PRICE</label><input value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} style={styles.input} type="number" /></div>
            <div style={styles.field}><label style={styles.label}>PURCHASE DATE</label><input value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} style={styles.input} type="date" /></div>
            {form.is_financed && <>
              <div style={styles.field}><label style={styles.label}>LENDER</label><input value={form.lender} onChange={e => set('lender', e.target.value)} style={styles.input} /></div>
              <div style={styles.field}><label style={styles.label}>ORIGINAL LOAN AMOUNT</label><input value={form.loan_amount} onChange={e => set('loan_amount', e.target.value)} style={styles.input} type="number" /></div>
              <div style={styles.field}><label style={styles.label}>INTEREST RATE (%)</label><input value={form.interest_rate} onChange={e => set('interest_rate', e.target.value)} style={styles.input} type="number" step="0.1" /></div>
              <div style={styles.field}><label style={styles.label}>LOAN BALANCE</label><input value={form.loan_balance} onChange={e => set('loan_balance', e.target.value)} style={styles.input} type="number" /></div>
              <div style={styles.field}><label style={styles.label}>MONTHLY PAYMENT</label><input value={form.monthly_payment} onChange={e => set('monthly_payment', e.target.value)} style={styles.input} type="number" /></div>
              <div style={styles.field}><label style={styles.label}>PAYMENT DUE DAY (1-31)</label><input value={form.payment_due_day} onChange={e => set('payment_due_day', e.target.value)} style={styles.input} type="number" min="1" max="31" /></div>
              <div style={styles.field}><label style={styles.label}>PAYOFF DATE</label><input value={form.loan_payoff_date} onChange={e => set('loan_payoff_date', e.target.value)} style={styles.input} type="date" /></div>
            </>}
          </div>
          <div style={styles.field}><label style={styles.label}>NOTES</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }} /></div>
          {error && <div style={styles.errorMsg}>{error}</div>}
        </div>
        <div style={styles.modalFooter}>
          <button onClick={onClose} style={styles.cancelBtn}>CANCEL</button>
          <button onClick={handleSave} disabled={saving} style={styles.saveBtn}>{saving ? 'SAVING...' : 'ADD EQUIPMENT'}</button>
        </div>
      </div>
    </div>
  )
}

function ServiceLogModal({ equipId, profileId, onClose, onSave }) {
  const [form, setForm] = useState({
    service_type: 'Oil Change',
    service_date: new Date().toISOString().split('T')[0],
    cost: '', notes: '', next_service_date: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleSave = async () => {
    setSaving(true)
    const { error: err } = await supabase.from('equipment_service_logs').insert({
      equipment_id: equipId,
      logged_by: profileId,
      service_type: form.service_type,
      service_date: form.service_date,
      cost: form.cost ? parseFloat(form.cost) : null,
      notes: form.notes || null,
      next_service_date: form.next_service_date || null,
    })
    if (err) { setError(err.message); setSaving(false); return }
    setSaving(false)
    onSave()
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={{ ...styles.modal, maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitle}>LOG SERVICE</div>
          <button onClick={onClose} style={styles.closeBtn}>{String.fromCodePoint(0x2715)}</button>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.editGrid}>
            <div style={styles.field}><label style={styles.label}>SERVICE TYPE</label>
              <select value={form.service_type} onChange={e => set('service_type', e.target.value)} style={styles.input}>
                {SERVICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={styles.field}><label style={styles.label}>DATE</label><input value={form.service_date} onChange={e => set('service_date', e.target.value)} style={styles.input} type="date" /></div>
            <div style={styles.field}><label style={styles.label}>COST</label><input value={form.cost} onChange={e => set('cost', e.target.value)} style={styles.input} type="number" placeholder="0.00" /></div>
            <div style={styles.field}><label style={styles.label}>NEXT SERVICE DUE</label><input value={form.next_service_date} onChange={e => set('next_service_date', e.target.value)} style={styles.input} type="date" /></div>
          </div>
          <div style={styles.field}><label style={styles.label}>NOTES</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }} /></div>
          {error && <div style={styles.errorMsg}>{error}</div>}
        </div>
        <div style={styles.modalFooter}>
          <button onClick={onClose} style={styles.cancelBtn}>CANCEL</button>
          <button onClick={handleSave} disabled={saving} style={styles.saveBtn}>{saving ? 'SAVING...' : 'LOG SERVICE'}</button>
        </div>
      </div>
    </div>
  )
}

function RentVsOwnCalc({ onClose }) {
  const [form, setForm] = useState({
    purchase_price: '', down_payment: '', loan_rate: '6.5', loan_term: '60',
    rental_day: '', rental_days_per_month: '10',
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const purchasePrice = parseFloat(form.purchase_price) || 0
  const downPayment = parseFloat(form.down_payment) || 0
  const loanAmount = purchasePrice - downPayment
  const monthlyRate = (parseFloat(form.loan_rate) || 0) / 100 / 12
  const term = parseInt(form.loan_term) || 60
  const monthlyPayment = monthlyRate > 0
    ? loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1)
    : loanAmount / term
  const rentalCostPerMonth = (parseFloat(form.rental_day) || 0) * (parseFloat(form.rental_days_per_month) || 0)
  const breakEvenMonths = rentalCostPerMonth > 0 ? Math.ceil(purchasePrice / rentalCostPerMonth) : null
  const fiveYearOwn = downPayment + (monthlyPayment * Math.min(term, 60))
  const fiveYearRent = rentalCostPerMonth * 60

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={{ ...styles.modal, maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitle}>RENT VS OWN CALCULATOR</div>
          <button onClick={onClose} style={styles.closeBtn}>{String.fromCodePoint(0x2715)}</button>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.calcSection}>PURCHASE DETAILS</div>
          <div style={styles.editGrid}>
            <div style={styles.field}><label style={styles.label}>PURCHASE PRICE</label><input value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} style={styles.input} type="number" placeholder="0" /></div>
            <div style={styles.field}><label style={styles.label}>DOWN PAYMENT</label><input value={form.down_payment} onChange={e => set('down_payment', e.target.value)} style={styles.input} type="number" placeholder="0" /></div>
            <div style={styles.field}><label style={styles.label}>LOAN RATE (%)</label><input value={form.loan_rate} onChange={e => set('loan_rate', e.target.value)} style={styles.input} type="number" step="0.1" /></div>
            <div style={styles.field}><label style={styles.label}>LOAN TERM (MONTHS)</label><input value={form.loan_term} onChange={e => set('loan_term', e.target.value)} style={styles.input} type="number" /></div>
          </div>
          <div style={styles.calcSection}>RENTAL DETAILS</div>
          <div style={styles.editGrid}>
            <div style={styles.field}><label style={styles.label}>RENTAL COST PER DAY</label><input value={form.rental_day} onChange={e => set('rental_day', e.target.value)} style={styles.input} type="number" placeholder="0" /></div>
            <div style={styles.field}><label style={styles.label}>DAYS USED PER MONTH</label><input value={form.rental_days_per_month} onChange={e => set('rental_days_per_month', e.target.value)} style={styles.input} type="number" /></div>
          </div>
          {purchasePrice > 0 && rentalCostPerMonth > 0 && (
            <div style={styles.calcResults}>
              <div style={styles.calcResultRow}>
                <span style={styles.calcResultLabel}>Monthly loan payment</span>
                <span style={styles.calcResultValue}>${Math.round(monthlyPayment).toLocaleString()}/mo</span>
              </div>
              <div style={styles.calcResultRow}>
                <span style={styles.calcResultLabel}>Monthly rental cost</span>
                <span style={styles.calcResultValue}>${Math.round(rentalCostPerMonth).toLocaleString()}/mo</span>
              </div>
              <div style={styles.calcDivider} />
              <div style={styles.calcResultRow}>
                <span style={styles.calcResultLabel}>5-year cost to own</span>
                <span style={styles.calcResultValue}>${Math.round(fiveYearOwn).toLocaleString()}</span>
              </div>
              <div style={styles.calcResultRow}>
                <span style={styles.calcResultLabel}>5-year cost to rent</span>
                <span style={styles.calcResultValue}>${Math.round(fiveYearRent).toLocaleString()}</span>
              </div>
              <div style={styles.calcDivider} />
              <div style={styles.calcHighlight}>
                <div style={styles.calcHighlightLabel}>BREAK EVEN POINT</div>
                <div style={styles.calcHighlightValue}>{breakEvenMonths} months ({Math.round(breakEvenMonths / 12 * 10) / 10} years)</div>
                <div style={styles.calcHighlightSub}>
                  {fiveYearOwn < fiveYearRent
                    ? 'Buying saves $' + Math.round(fiveYearRent - fiveYearOwn).toLocaleString() + ' over 5 years'
                    : 'Renting saves $' + Math.round(fiveYearOwn - fiveYearRent).toLocaleString() + ' over 5 years'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const styles = {
  container: { fontFamily: "'Barlow',sans-serif", maxWidth: '100%' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
  title: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  subtitle: { fontSize: '15px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' },
  headerBtns: { display: 'flex', gap: '10px' },
  calcBtn: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', padding: '12px 20px', color: 'rgba(255,255,255,0.6)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  addBtn: { backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', padding: '12px 20px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  empty: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '15px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.05)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '16px' },
  equipCard: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '20px', cursor: 'pointer' },
  equipHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  equipType: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)' },
  ownershipBadge: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '1px' },
  equipName: { fontSize: '18px', fontWeight: 600, color: '#ffffff', marginBottom: '4px' },
  equipSub: { fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginBottom: '10px' },
  equipValue: { fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' },
  valuationAlert: { color: '#e05c5c', fontSize: '11px' },
  equipPayment: { fontSize: '13px', color: 'rgba(255,255,255,0.4)' },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 400, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', padding: '20px' },
  detailCard: { width: '520px', maxWidth: '100%', height: 'calc(100vh - 40px)', backgroundColor: '#0d1420', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px', display: 'flex', flexDirection: 'column', overflowY: 'auto', fontFamily: "'Barlow',sans-serif" },
  detailHeader: { padding: '24px 24px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '20px' },
  detailHeaderRight: { display: 'flex', gap: '8px', alignItems: 'center' },
  detailType: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '4px' },
  detailName: { fontSize: '22px', fontWeight: 600, color: '#ffffff', marginBottom: '4px' },
  detailSub: { fontSize: '14px', color: 'rgba(255,255,255,0.4)' },
  tabs: { display: 'flex', gap: '2px', padding: '0 24px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  tab: { padding: '12px 16px', backgroundColor: 'transparent', border: 'none', color: 'rgba(255,255,255,0.35)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', letterSpacing: '1.5px', cursor: 'pointer', borderBottom: '2px solid transparent' },
  tabActive: { color: '#b48c3c', borderBottom: '2px solid #b48c3c' },
  detailBody: { flex: 1, padding: '20px 24px', overflowY: 'auto' },
  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' },
  infoItem: {},
  infoLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.25)', marginBottom: '3px' },
  infoValue: { fontSize: '15px', color: '#ffffff' },
  notes: { fontSize: '14px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, backgroundColor: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '3px' },
  editForm: { display: 'flex', flexDirection: 'column', gap: '16px' },
  editGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.35)' },
  input: { backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '10px 12px', color: '#ffffff', fontSize: '14px', fontFamily: "'Barlow',sans-serif", outline: 'none', width: '100%', boxSizing: 'border-box' },
  editActions: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  cancelBtn: { padding: '10px 18px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', letterSpacing: '2px', cursor: 'pointer' },
  saveBtn: { padding: '10px 20px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  addLogBtn: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', padding: '10px 16px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', letterSpacing: '1.5px', cursor: 'pointer', marginBottom: '16px' },
  emptyTab: { color: 'rgba(255,255,255,0.2)', textAlign: 'center', padding: '40px', fontSize: '14px' },
  logRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  logLeft: { flex: 1 },
  logType: { fontSize: '15px', color: '#ffffff', marginBottom: '3px' },
  logDate: { fontSize: '12px', color: 'rgba(255,255,255,0.35)', marginBottom: '4px' },
  logNotes: { fontSize: '13px', color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 },
  logNext: { fontSize: '12px', color: '#b48c3c', marginTop: '4px' },
  logCost: { fontSize: '16px', fontWeight: 600, color: '#b48c3c', flexShrink: 0, marginLeft: '16px' },
  valueActions: { marginBottom: '20px' },
  estimateBtn: { backgroundColor: 'rgba(180,140,60,0.1)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', padding: '12px 20px', color: '#b48c3c', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer', width: '100%' },
  currentValue: { backgroundColor: 'rgba(180,140,60,0.06)', border: '1px solid rgba(180,140,60,0.2)', borderRadius: '4px', padding: '20px', marginBottom: '20px', textAlign: 'center' },
  currentValueLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '8px' },
  currentValueAmount: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '40px', fontWeight: 700, color: '#b48c3c', lineHeight: 1, marginBottom: '4px' },
  currentValueDate: { fontSize: '12px', color: 'rgba(255,255,255,0.3)' },
  modal: { backgroundColor: '#0d1420', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', width: '100%', maxWidth: '720px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', fontFamily: "'Barlow',sans-serif", position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  modalTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '18px', fontWeight: 700, letterSpacing: '3px', color: '#b48c3c' },
  closeBtn: { backgroundColor: 'transparent', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '18px', cursor: 'pointer' },
  modalBody: { flex: 1, overflowY: 'auto', padding: '24px' },
  modalFooter: { display: 'flex', gap: '12px', justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.06)' },
  errorMsg: { backgroundColor: 'rgba(220,60,60,0.1)', border: '1px solid rgba(220,60,60,0.3)', borderRadius: '3px', padding: '10px 14px', color: '#ff6b6b', fontSize: '14px', marginTop: '12px' },
  editBtn: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '1.5px', padding: '5px 10px', cursor: 'pointer' },
  calcSection: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '3px', color: 'rgba(255,255,255,0.3)', marginBottom: '12px', marginTop: '16px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  calcResults: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '20px', marginTop: '20px' },
  calcResultRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  calcResultLabel: { fontSize: '14px', color: 'rgba(255,255,255,0.5)' },
  calcResultValue: { fontSize: '16px', color: '#ffffff', fontWeight: 600 },
  calcDivider: { height: '1px', backgroundColor: 'rgba(255,255,255,0.06)', margin: '12px 0' },
  calcHighlight: { backgroundColor: 'rgba(180,140,60,0.08)', border: '1px solid rgba(180,140,60,0.2)', borderRadius: '3px', padding: '16px', marginTop: '12px', textAlign: 'center' },
  calcHighlightLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px' },
  calcHighlightValue: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '28px', fontWeight: 700, color: '#b48c3c', marginBottom: '4px' },
  calcHighlightSub: { fontSize: '13px', color: 'rgba(255,255,255,0.5)' },
}
